import type { ProspectFilterState } from "./prospectFilterUtils";

/**
 * Oversetter prospektsidens filtertilstand til et PostgREST-filter (etappe 3).
 *
 * HVORFOR EN EGEN, REN MODUL. Dette er stedet der en feil ikke synes: et
 * filter som skrives litt galt gir ikke en feilmelding, det gir en kortere
 * liste. `status_flyt=neq.dismissed` ville for eksempel sett riktig ut og
 * stille utelatt alle enheter som aldri har fått en `prospects`-rad — de aller
 * fleste av dem. Derfor bor oversettelsen her, uten React og uten nettverk, og
 * har tester per felt.
 *
 * Kolonnene den filtrerer på er utledet i `prospect_overview`
 * (20260730100000_prospekt_serverfilter.sql): `status_flyt`, `naering2`,
 * `ansatte_bucket`, `plattform`, `ignorert`. Det er de fire klassifiseringene
 * som finnes både i TypeScript og i SQL, og migrasjonens hovedkommentar sier
 * hvorfor og hva vernet er.
 */

/** Fasett-tellingene fra `prospekt_fasetter()`. */
export type FasettVerdi = { v: string; n: number };
export type FasettKommune = FasettVerdi & { navn?: string | null };
export type FasettNaering = FasettVerdi & { tekst?: string | null };

export type Fasetter = {
  /** Hele universet, uavhengig av filtre. */
  totalt: number;
  ignorerte: number;
  status: FasettVerdi[];
  kilde: FasettVerdi[];
  kommune: FasettKommune[];
  naering: FasettNaering[];
  ansatte: FasettVerdi[];
  plattform: FasettVerdi[];
};

export const TOMME_FASETTER: Fasetter = {
  totalt: 0,
  ignorerte: 0,
  status: [],
  kilde: [],
  kommune: [],
  naering: [],
  ansatte: [],
  plattform: [],
};

/**
 * Sorteringen lista bruker.
 *
 * `rangering`, ikke `score`: PostgREST sorterer på ett felt, og score alene er
 * ikke en entydig rekkefølge. Med hundrevis av rader på samme score kan
 * Postgres gi dem i ulik orden fra spørring til spørring, og da kan én rad
 * dukke opp på to sider mens en annen aldri vises. `rangering` har org.nr
 * bakt inn som tiebreaker. Se viewets kommentar.
 */
export const PROSPEKT_SORTERING = {
  field: "rangering",
  order: "DESC" as const,
};

/**
 * Verdier i en `in`-liste kvoteres.
 *
 * «50+» og «2-9» er ansatt-bøtter, og et pluss-tegn i en URL-parameter er den
 * klassiske stille feilen (det kan bli et mellomrom). Doble anførselstegn er
 * PostgRESTs egen kvotering av listeelementer, og den fjerner hele spørsmålet.
 */
const inListe = (verdier: string[]): string =>
  `("${verdier.map((v) => v.replace(/"/g, '\\"')).join('","')}")`;

export type ProspektListeFilter = Record<string, unknown>;

export const filterTilPostgrest = (
  f: ProspectFilterState,
  { sok, visIgnorerte }: { sok: string; visIgnorerte: boolean },
): ProspektListeFilter => {
  const ut: ProspektListeFilter = {};

  // Ignorerte skjules med et eget boolsk flagg fra viewet. Se kommentaren i
  // migrasjonen: `prospect_status != 'dismissed'` ville utelatt null-radene.
  if (!visIgnorerte) ut["ignorert@eq"] = false;

  // Ignorerte rader har `status_flyt = null` og passerer derfor aldri et
  // aktivt statusfilter — samme regel som klienten hadde, nå i én kolonne.
  if (f.statuser.length) ut["status_flyt@in"] = inListe(f.statuser);
  if (f.kommuner.length) ut["kommunenr@in"] = inListe(f.kommuner);
  if (f.naeringer.length) ut["naering2@in"] = inListe(f.naeringer);
  if (f.ansatte.length) ut["ansatte_bucket@in"] = inListe(f.ansatte);
  if (f.plattform.length) ut["plattform@in"] = inListe(f.plattform);
  // Rader uten kilde passerer ikke et aktivt kildefilter: en ukjent kilde er
  // ikke «alle kilder». En `in`-liste treffer aldri null, så dette følger av
  // seg selv — men det er verdt å vite at det er med vilje.
  if (f.kilder.length) ut["kilde@in"] = inListe(f.kilder);

  // Uskannede rader har score null og faller ut av et aktivt temperaturfilter.
  // Det er riktig: «minst 40» kan ikke oppfylles av en rad vi ikke har målt.
  if (f.minScore > 0) ut["score@gte"] = f.minScore;
  if (f.kunManglerNettside) ut["url@is"] = "null";
  // Rader uten regnskapstall faller ut av et aktivt omsetningsfilter, som de
  // uskannede gjør av temperaturfilteret: «minst 5 MNOK» kan ikke oppfylles av
  // en rad vi ikke har tall for. Enheter hentet via prospektsøket har ingen
  // regnskapstall — de kommer bare fra Proff-fila.
  if (f.minOmsetning > 0) ut["omsetning@gte"] = f.minOmsetning;

  // `q` omsettes til ilike-tre av applyFullTextSearch-callbacken for
  // prospect_overview i dataProvider.ts. Blankt søk sendes ikke i det hele
  // tatt — et tomt q ville gitt et filter som matcher tom streng.
  const q = sok.trim();
  if (q) ut.q = q;

  return ut;
};

/** Tellingen som hører til én fasett-verdi, eller 0. */
export const fasettAntall = (liste: FasettVerdi[], verdi: string): number =>
  liste.find((f) => f.v === verdi)?.n ?? 0;
